import type { FormInstance } from '@arco-design/web-react/es/Form';
import get from 'lodash/get';

import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { commaTextToStartupArray } from '@/components-biz/ImageSelect';

import { parseServicesFromImageItem } from '../../../capabilities/image-vm/applyImageServices';
import type { ServiceFormItem } from '../../../capabilities/service/types';
import { type StartupArgDefinition, apiTokensToStartupArgFormRows } from '../../../capabilities/startup-command-args/utils';
import {
  SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD,
  normalizeContainerDeployConfigStorage,
} from '../../../platform/container-deploy-config';
import { peekResourceFormContext } from '../../../platform/resource-form/formContextRegistry';
import {
  buildContainerDeploySystemOverlay,
  buildImageDeployVolumePresets,
  buildStartupArgFormRowsFromDeployConfigArgs,
  normalizeContainerDeployInstanceScheduling,
  parseContainerDeployConfigObject,
  readDeployConfigStartupCommand,
  readStartupArgDefinitionsFromDeployConfig,
  resolveImageDeployVolumePaths,
} from '../../_shared/common/container-deploy-config/containerDeployConfig';
import { setFieldValueIfChanged } from '../../_shared/common/formHelpers';
import { enableSkuModulesForPreset } from '../../_shared/common/sku/skuModulePreset';
import { CI_DEPLOY_SYSTEM_FIELD, CI_SERVICES_PATH, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD } from './constants';

export type { ContainerDeployInstanceScheduling } from '../../_shared/common/container-deploy-config/containerDeployConfig';

export interface CiDeployConfigBinding {
  imagePrefix: string;
  servicesPath: string;
  systemField: string;
  volumesPath: string;
}

export const CI_DEFAULT_DEPLOY_CONFIG_BINDING: CiDeployConfigBinding = {
  imagePrefix: 'basic.image',
  servicesPath: CI_SERVICES_PATH,
  systemField: CI_DEPLOY_SYSTEM_FIELD,
  volumesPath: 'volumes',
};

export interface ApplyContainerDeployConfigOptions {
  clearResourceCompute?: boolean;
  skipClear?: boolean;
  /**
   * 下单选/换/清镜像：以 SKU preset 为底合并新镜像列表并去重，移除非 SKU 的旧镜像派生行。
   * 标量字段：SKU 有值则保留，否则用新镜像 / 清空。
   */
  mergeWithSkuPreset?: boolean;
  /** 可选；缺省从 Form 绑定的 ResourceFormContext.presetConfigSnapshot 读取 */
  skuPresetSnapshot?: Record<string, unknown>;
}

function startupArgsToFormRows(args: unknown[]) {
  if (!args.length) return [];
  if (typeof args[0] === 'object' && args[0] !== null) {
    return buildStartupArgFormRowsFromDeployConfigArgs(args);
  }
  if (typeof args[0] === 'string') {
    return apiTokensToStartupArgFormRows(commaTextToStartupArray(args.join(',')));
  }
  return apiTokensToStartupArgFormRows(args.map(String));
}

function normalizeEnvVars(raw: unknown): { name: string; value: string; _self?: string }[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as { key?: string; name?: string; value?: unknown };
      const name = String(row.key ?? row.name ?? '').trim();
      if (!name) return null;
      const value = row.value != null ? String(row.value) : '';
      const meta = { ...row, key: name, name, value };
      return { name, value, _self: JSON.stringify(meta) };
    })
    .filter((item): item is { name: string; value: string; _self: string } => Boolean(item));
}

function fieldPath(prefix: string, segment: string) {
  return prefix ? `${prefix}.${segment}` : segment;
}

function clearAllVolumes(form: FormInstance, volumesPath: string) {
  setFieldValueIfChanged(form, volumesPath, undefined);
}

/** 从镜像 containerDeployConfig 解析启动参数定义（供 startup-command-args 预置下拉） */
export function readStartupArgDefinitionsFromImage(imageItem: CommonImageItemType): StartupArgDefinition[] {
  return readStartupArgDefinitionsFromDeployConfig(imageItem.containerDeployConfig);
}

/** 切换镜像前清除由 containerDeployConfig 写入的字段 */
export function clearContainerDeployConfigFromForm(form: FormInstance, binding: CiDeployConfigBinding) {
  setFieldValueIfChanged(form, binding.servicesPath, undefined);
  setFieldValueIfChanged(form, fieldPath(binding.imagePrefix, 'command'), undefined);
  setFieldValueIfChanged(form, fieldPath(binding.imagePrefix, 'args'), undefined);
  setFieldValueIfChanged(form, fieldPath(binding.imagePrefix, 'startupArgs'), undefined);
  setFieldValueIfChanged(form, fieldPath(binding.imagePrefix, 'env'), undefined);
  setFieldValueIfChanged(form, SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD, undefined);
  clearAllVolumes(form, binding.volumesPath);
  setFieldValueIfChanged(form, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, undefined);
  if (binding.systemField) {
    setFieldValueIfChanged(form, `${binding.systemField}.security`, undefined);
    setFieldValueIfChanged(form, `${binding.systemField}.devices`, undefined);
  }
}

function isEmptyFormValue(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function readPresetPath(preset: Record<string, unknown> | undefined, path: string): unknown {
  if (!preset) return undefined;
  const nested = get(preset, path);
  if (nested !== undefined) return nested;
  return preset[path];
}

function readPresetList(preset: Record<string, unknown> | undefined, path: string): unknown[] {
  const value = readPresetPath(preset, path);
  return Array.isArray(value) ? value : [];
}

/** 列表按 key 合并去重；base 优先（SKU），再追加 incoming 中未见过的 key */
function mergeNamedRows<T>(base: T[], incoming: T[], resolveKey: (row: T) => string): T[] {
  const map = new Map<string, T>();
  for (const row of base) {
    const key = resolveKey(row).trim();
    if (key && !map.has(key)) map.set(key, row);
  }
  for (const row of incoming) {
    const key = resolveKey(row).trim();
    if (!key || map.has(key)) continue;
    map.set(key, row);
  }
  return Array.from(map.values());
}

function resolveNamedRowKey(row: unknown, keyField: string): string {
  if (!row || typeof row !== 'object') return '';
  return String((row as Record<string, unknown>)[keyField] ?? '').trim();
}

function resolveVolumeRowKey(row: unknown): string {
  if (!row || typeof row !== 'object') return '';
  const r = row as { volumeName?: string; volumeMounts?: { mountPath?: string }[] };
  const mount = String(r.volumeMounts?.[0]?.mountPath ?? '').trim();
  if (mount) return mount;
  return String(r.volumeName ?? '').trim();
}

function resolveSkuPresetSnapshot(form: FormInstance, options?: ApplyContainerDeployConfigOptions): Record<string, unknown> {
  if (options?.skuPresetSnapshot && typeof options.skuPresetSnapshot === 'object') {
    return options.skuPresetSnapshot;
  }
  return peekResourceFormContext(form)?.presetConfigSnapshot ?? {};
}

/** 选中镜像后，将 containerDeployConfig 映射到服务 / 启动参数 / 环境变量 / 数据盘 / 调度约束 / system */
export function applyContainerDeployConfigToForm(
  form: FormInstance,
  imageItem: CommonImageItemType | null | undefined,
  binding: CiDeployConfigBinding = CI_DEFAULT_DEPLOY_CONFIG_BINDING,
  options?: ApplyContainerDeployConfigOptions,
) {
  if (!options?.skipClear) {
    clearContainerDeployConfigFromForm(form, binding);
  }

  const mergeWithSkuPreset = options?.mergeWithSkuPreset === true;
  const skuPreset = mergeWithSkuPreset ? resolveSkuPresetSnapshot(form, options) : undefined;
  const safeImage = imageItem ?? ({} as CommonImageItemType);

  const config = parseContainerDeployConfigObject(safeImage.containerDeployConfig);
  const rawConfig = safeImage.containerDeployConfig;
  const storedConfig = normalizeContainerDeployConfigStorage(rawConfig);

  const commandPath = fieldPath(binding.imagePrefix, 'command');
  const argsPath = fieldPath(binding.imagePrefix, 'startupArgs');
  const envPath = fieldPath(binding.imagePrefix, 'env');
  const securityPath = `${binding.systemField}.security`;
  const devicesPath = `${binding.systemField}.devices`;

  const imageServices = (parseServicesFromImageItem(safeImage) as ServiceFormItem[] | undefined) ?? [];
  const startupItem = readDeployConfigStartupCommand(safeImage.containerDeployConfig);
  const imageStartupArgs = startupItem?.args?.length ? startupArgsToFormRows(startupItem.args) : [];
  const imageEnvVars = normalizeEnvVars(config.envVars);
  const volumePaths = resolveImageDeployVolumePaths(safeImage);
  const containerName = String(form.getFieldValue('name') ?? '').trim();
  const imageVolumePresets = buildImageDeployVolumePresets(volumePaths, containerName);
  const systemOverlay = buildContainerDeploySystemOverlay(config);
  const scheduling = normalizeContainerDeployInstanceScheduling(config.instanceScheduling);

  let nextServices: ServiceFormItem[] | undefined;
  let nextStartupArgs: object[] | undefined;
  let nextEnv: { name: string; value: string; _self?: string }[] | undefined;
  let nextVolumes: unknown[] | undefined;

  if (mergeWithSkuPreset) {
    const skuServices = readPresetList(skuPreset, binding.servicesPath);
    const legacySkuServices = skuServices.length ? [] : readPresetList(skuPreset, 'resource.network.service');
    nextServices = mergeNamedRows([...skuServices, ...legacySkuServices] as ServiceFormItem[], imageServices, (row) =>
      resolveNamedRowKey(row, 'name'),
    );

    const skuArgs = readPresetList(skuPreset, argsPath);
    const skuArgsAlt = skuArgs.length ? [] : readPresetList(skuPreset, fieldPath(binding.imagePrefix, 'args'));
    nextStartupArgs = mergeNamedRows([...skuArgs, ...skuArgsAlt] as object[], imageStartupArgs as object[], (row) =>
      resolveNamedRowKey(row, 'name'),
    );

    const skuEnv = normalizeEnvVars(readPresetPath(skuPreset, envPath));
    nextEnv = mergeNamedRows(skuEnv, imageEnvVars, (row) => resolveNamedRowKey(row, 'name'));

    const skuVolumes = readPresetList(skuPreset, binding.volumesPath);
    nextVolumes = mergeNamedRows(skuVolumes, imageVolumePresets, resolveVolumeRowKey);

    const skuCommand = readPresetPath(skuPreset, commandPath);
    if (!isEmptyFormValue(skuCommand)) {
      setFieldValueIfChanged(form, commandPath, skuCommand);
    } else if (startupItem?.commandText) {
      setFieldValueIfChanged(form, commandPath, startupItem.commandText);
    } else {
      setFieldValueIfChanged(form, commandPath, undefined);
    }

    setFieldValueIfChanged(form, binding.servicesPath, nextServices.length ? nextServices : undefined);
    setFieldValueIfChanged(form, argsPath, nextStartupArgs.length ? nextStartupArgs : undefined);
    setFieldValueIfChanged(form, envPath, nextEnv.length ? nextEnv : undefined);
    setFieldValueIfChanged(form, binding.volumesPath, nextVolumes.length ? nextVolumes : undefined);

    if (storedConfig) {
      setFieldValueIfChanged(form, SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD, storedConfig);
    } else {
      setFieldValueIfChanged(form, SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD, undefined);
    }

    const skuSecurity = readPresetPath(skuPreset, securityPath);
    if (skuSecurity != null && skuSecurity !== '') {
      setFieldValueIfChanged(form, securityPath, skuSecurity);
    } else if (systemOverlay?.security) {
      setFieldValueIfChanged(form, securityPath, systemOverlay.security);
    } else {
      setFieldValueIfChanged(form, securityPath, undefined);
    }

    const skuDevices = readPresetPath(skuPreset, devicesPath);
    if (skuDevices != null && skuDevices !== '') {
      setFieldValueIfChanged(form, devicesPath, skuDevices);
    } else if (systemOverlay?.devices) {
      const devices = { ...(systemOverlay.devices as Record<string, unknown>) };
      delete devices.sharedMemory;
      setFieldValueIfChanged(form, devicesPath, Object.keys(devices).length ? devices : undefined);
    } else {
      setFieldValueIfChanged(form, devicesPath, undefined);
    }

    const skuScheduling = readPresetPath(skuPreset, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD);
    if (skuScheduling != null && skuScheduling !== '') {
      setFieldValueIfChanged(form, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, skuScheduling);
    } else if (scheduling) {
      setFieldValueIfChanged(form, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, scheduling);
    } else {
      setFieldValueIfChanged(form, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, undefined);
    }
  } else {
    if (storedConfig) {
      setFieldValueIfChanged(form, SYSTEM_OPTIONS_CONTAINER_DEPLOY_CONFIG_FIELD, storedConfig);
    }
    if (imageServices.length) {
      nextServices = imageServices;
      setFieldValueIfChanged(form, binding.servicesPath, imageServices);
    }
    if (startupItem?.commandText) {
      setFieldValueIfChanged(form, commandPath, startupItem.commandText);
    }
    if (imageStartupArgs.length) {
      nextStartupArgs = imageStartupArgs as object[];
      setFieldValueIfChanged(form, argsPath, imageStartupArgs.length ? imageStartupArgs : undefined);
    }
    if (imageEnvVars.length) {
      nextEnv = imageEnvVars;
      setFieldValueIfChanged(form, envPath, imageEnvVars);
    }
    if (imageVolumePresets.length) {
      nextVolumes = imageVolumePresets;
      setFieldValueIfChanged(form, binding.volumesPath, imageVolumePresets);
    }
    if (systemOverlay?.security) {
      setFieldValueIfChanged(form, securityPath, systemOverlay.security);
    }
    if (systemOverlay?.devices) {
      const devices = { ...(systemOverlay.devices as Record<string, unknown>) };
      delete devices.sharedMemory;
      if (Object.keys(devices).length) {
        setFieldValueIfChanged(form, devicesPath, devices);
      }
    }
    if (scheduling) {
      setFieldValueIfChanged(form, CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD, scheduling);
    }
  }

  const modulesToEnable: string[] = [];
  const servicesLen = nextServices?.length ?? (mergeWithSkuPreset ? 0 : imageServices.length);
  const envLen = nextEnv?.length ?? (mergeWithSkuPreset ? 0 : imageEnvVars.length);
  const argsLen = nextStartupArgs?.length ?? (mergeWithSkuPreset ? 0 : imageStartupArgs.length);
  const volumesLen = nextVolumes?.length ?? (mergeWithSkuPreset ? 0 : imageVolumePresets.length);
  const hasCommand = mergeWithSkuPreset ? !isEmptyFormValue(form.getFieldValue(commandPath)) : Boolean(startupItem?.commandText);
  if (servicesLen) modulesToEnable.push('service');
  if (envLen) modulesToEnable.push('envVars');
  if (hasCommand || argsLen) modulesToEnable.push('startupCommandArgs');
  if (volumesLen) modulesToEnable.push('storage');
  if (form.getFieldValue(CONTAINER_DEPLOY_INSTANCE_SCHEDULING_FIELD)) modulesToEnable.push('resource');
  if (form.getFieldValue(securityPath)) modulesToEnable.push('systemSetting');
  enableSkuModulesForPreset(form, modulesToEnable);
}
